import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { ClipboardList, CheckCircle2 } from 'lucide-react';
import { Card } from '@components/base/layout';
import { actionAnchorId } from '../configs/otherActionsConfig';
import { Text, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { EntityForm, useEntityForm } from '@components/integrated/entity-form';
import type {
  EntityFormConfig,
  SelectFieldOptions,
  DateFieldOptions,
  TextareaOptions,
} from '@components/integrated/entity-form';
import {
  applyLayoutOverrides,
  buildPublishedConfigFromEntityForm,
  resolveCardTitle,
  usePublishLayoutConfig,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import { claimflowApiClient } from '../api/claimflowApiClient';
import { useClaimFlowAuthStore } from '../auth/store';
import { useClaimDataStore } from './claimDataStore';

interface PlanificateurOption {
  ID_PLANIF: number;
  NOM: string;
}

interface TypeTacheOption {
  CODE_TYPE: string;
  LIBELLE_TYPE: string;
}

interface ErpProxyResponse<T> {
  success: boolean;
  data: { total: number; data: T[] };
}

interface CreateTaskCardProps {
  ses: string;
  idSin: string;
  ncbtSin: string;
  /**
   * Per-tenant field layout overrides injected by the `withComponentRules`
   * HOC. Underscored to match the injected meta-prop convention; `undefined`
   * at baseline ⇒ the form config is returned unchanged (byte-identical
   * render).
   */
  _layoutOverrides?: LayoutOverrides;
}

export function CreateTaskCard({
  ses,
  idSin,
  ncbtSin,
  _layoutOverrides,
}: Readonly<CreateTaskCardProps>) {
  const cdUtil = useClaimFlowAuthStore((s) => s.cdUtil);
  const [planificateurs, setPlanificateurs] = useState<PlanificateurOption[]>(
    [],
  );
  const [typeTaches, setTypeTaches] = useState<TypeTacheOption[]>([]);
  // Collapsed on first paint — every card in the claim view's right column
  // opens folded. Driven by state + `onExpandChange` rather than a literal
  // `defaultExpanded={false}`, because `Card` re-applies a literal default on
  // every parent re-render, which would fold the card again mid-edit.
  const [expanded, setExpanded] = useState(false);

  const [submitted, setSubmitted] = useState(false);
  const [submittedAssignee, setSubmittedAssignee] = useState('');
  const lastSchedulerIdRef = useRef<string>('');

  useEffect(() => {
    let cancelled = false;
    async function fetchPlanificateurs() {
      try {
        const response = await claimflowApiClient.get<
          ErpProxyResponse<PlanificateurOption>
        >(
          '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-planificateur',
          { params: { SES: ses, perPage: 9999 } },
        );
        if (!cancelled && response.data.success) {
          setPlanificateurs(response.data.data.data);
        }
      } catch {
        // silently fail
      }
    }
    fetchPlanificateurs();
    return () => {
      cancelled = true;
    };
  }, [ses]);

  useEffect(() => {
    let cancelled = false;
    async function fetchTypeTaches() {
      try {
        const response = await claimflowApiClient.get<
          ErpProxyResponse<TypeTacheOption>
        >(
          '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-typetache',
          { params: { SES: ses, perPage: 250 } },
        );
        if (!cancelled && response.data.success) {
          setTypeTaches(response.data.data.data);
        }
      } catch {
        // silently fail
      }
    }
    fetchTypeTaches();
    return () => {
      cancelled = true;
    };
  }, [ses]);

  const mutationFn = useCallback(
    async (payload: Record<string, unknown> | FormData) => {
      const data =
        payload instanceof FormData
          ? Object.fromEntries(payload.entries())
          : payload;

      const dateVal = data.date;
      let formattedDate = '';
      if (dateVal instanceof Date) {
        const dd = String(dateVal.getDate()).padStart(2, '0');
        const mm = String(dateVal.getMonth() + 1).padStart(2, '0');
        const yyyy = dateVal.getFullYear();
        formattedDate = `${dd}/${mm}/${yyyy} 00:00`;
      } else if (typeof dateVal === 'string') {
        formattedDate = dateVal;
      }

      lastSchedulerIdRef.current = String(data.schedulerId ?? '');

      const response = await claimflowApiClient.post(
        '/claimflow/erp/registries/veos/endpoints/task-new',
        {
          data: {
            schedulerId: String(data.schedulerId ?? ''),
            date: formattedDate,
            text: String(data.text ?? ''),
            priority: 'STANDARD',
            creator: cdUtil ?? '',
            taskType: String(data.taskType ?? ''),
            maxProgress: '0',
            entity: {
              codeEntity: ncbtSin,
              typeEntity: 'SINISTRE',
            },
          },
        },
        { params: { SES: ses } },
      );
      return response.data;
    },
    [ses, idSin, ncbtSin, cdUtil],
  );

  const baseConfig: EntityFormConfig = useMemo(
    () => ({
      id: 'create-task',
      endpoint: {
        erpEndpoint:
          '/claimflow/erp/registries/veos/endpoints/task-new',
        method: 'POST',
      },
      fields: {
        schedulerId: {
          type: 'select',
          label: 'Affectation',
          order: 1,
          validation: { required: true },
          options: {
            items: planificateurs.map((p) => ({
              value: String(p.ID_PLANIF),
              label: p.NOM,
            })),
            searchable: true,
            placeholder: 'Sélectionner une personne',
          } as SelectFieldOptions,
        },
        date: {
          type: 'date',
          label: 'Date de début',
          order: 2,
          validation: { required: true },
          placeholder: 'Sélectionner une date',
          options: {
            displayFormat: 'dd/MM/yyyy',
          } as DateFieldOptions,
        },
        taskType: {
          type: 'select',
          label: 'Catégorie',
          order: 3,
          validation: { required: true },
          options: {
            items: typeTaches.map((t) => ({
              value: t.CODE_TYPE,
              label: t.LIBELLE_TYPE,
            })),
            searchable: true,
            placeholder: 'Sélectionner une catégorie',
          } as SelectFieldOptions,
        },
        text: {
          type: 'textarea',
          label: 'Commentaire',
          order: 4,
          validation: { required: true },
          placeholder: 'Ajouter un commentaire...',
          options: {
            rows: 4,
          } as TextareaOptions,
        },
      },
      actions: {
        submit: { visible: true, label: 'Créer la tâche' },
        cancel: { visible: false },
        reset: { visible: false },
      },
      layout: { columns: 1, gap: 'sm' },
    }),
    [planificateurs, typeTaches],
  );

  usePublishLayoutConfig(
    'claimflow.createTaskCard',
    buildPublishedConfigFromEntityForm(baseConfig, 'Créer une tâche'),
  );

  const formConfig: EntityFormConfig = useMemo(
    () => applyLayoutOverrides(baseConfig, _layoutOverrides),
    [baseConfig, _layoutOverrides],
  );

  const form = useEntityForm({
    config: formConfig,
    mutationFn,
    onSuccess: () => {
      const match = planificateurs.find(
        (p) => String(p.ID_PLANIF) === lastSchedulerIdRef.current,
      );
      setSubmittedAssignee(match?.NOM ?? '');
      setSubmitted(true);
      // The claim has a new task — the « Tâches » card re-fetches its list
      // (mahhp 22862). `onSuccess` only runs once `task-new` has resolved.
      useClaimDataStore.getState().bumpTasks();
    },
  });

  if (submitted) {
    return (
      <Card key="submitted" id={actionAnchorId('claimflow.createTaskCard')}>
        <Card.Header collapsible defaultExpanded={false}>
          <Icon icon={CheckCircle2} size="md" color="success" />
          <Text as="span" weight="semibold" size="base">
            {resolveCardTitle('Créer une tâche', _layoutOverrides)}
          </Text>
          <Text as="span" size="sm" color="muted" className="ml-2">
            ({submittedAssignee})
          </Text>
        </Card.Header>
        <Card.Content>
          <div className="pt-4">
            <Button
              variant="primary"
              disabled
              className="w-full opacity-60"
            >
              Tâche créée
            </Button>
          </div>
        </Card.Content>
      </Card>
    );
  }

  return (
    <Card id={actionAnchorId('claimflow.createTaskCard')}>
      <Card.Header
        collapsible
        defaultExpanded={expanded}
        onExpandChange={setExpanded}
      >
        <Icon icon={ClipboardList} size="md" color="primary" />
        <Text as="span" weight="semibold" size="base">
          {resolveCardTitle('Créer une tâche', _layoutOverrides)}
        </Text>
      </Card.Header>
      <Card.Content>
        <EntityForm config={formConfig} form={form}>
          <EntityForm.Alert />
          <EntityForm.Fields />
          {formConfig.actions?.submit?.visible !== false && (
            <div className="pt-4">
              <Button
                type="submit"
                variant="primary"
                loading={form.isSubmitting}
                disabled={form.isSubmitting}
                className="w-full"
              >
                {formConfig.actions?.submit?.label ?? 'Créer la tâche'}
              </Button>
            </div>
          )}
        </EntityForm>
      </Card.Content>
    </Card>
  );
}
